'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();

import { useInterval } from '@qogita/react-hooks/use-interval'
import { cn } from '@qogita/ui/utils/cn'
import { cva } from 'class-variance-authority'
import {
  ComponentProps,
  createContext,
  CSSProperties,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

const ProgressBarContext = createContext<ReturnType<typeof useProgress> | null>(
  null,
)

type ProgressState = 'initial' | 'in-progress' | 'completing' | 'complete'

const progressBarStyles = cva('border-b', {
  variants: {
    state: {
      initial: 'border-transparent bg-transparent',
      'in-progress': 'border-b-gray-50 bg-gray-50',
      completing: 'border-b-gray-50 bg-gray-50',
      complete: 'border-b-gray-50 bg-gray-50',
    },
  },
})

export function useProgressBar() {
  const defaultValue = {
    startTransition: (action: () => void | Promise<void>) => {
      startTransition(async () => {
        await action()
      })
    },
  }

  const progress = useContext(ProgressBarContext) ?? defaultValue
  return progress
}

/**
 * Renders a progress bar that will show if any `<ProgressBarLink>` inside it is navigating.
 *
 * @see https://buildui.com/posts/global-progress-in-nextjs
 */
export function ProgressBar({
  children,
  className,
  'aria-live': ariaLive = 'polite',
  ...props
}: Omit<ComponentProps<'div'>, 'aria-busy' | 'role'>) {
  const progress = useProgress()

  return (
    <ProgressBarContext.Provider value={progress}>
      {progress.state === 'complete' ? null : (
        <div
          className={cn(
            progressBarStyles({
              state: progress.state,
            }),
            className,
          )}
          style={
            {
              '--progress-value': progress.value,
            } as CSSProperties
          }
          role="progressbar"
          aria-live={ariaLive}
          aria-busy={progress.state === 'in-progress'}
          {...props}
        >
          <div className="h-full origin-left [transform:scaleX(calc(var(--progress-value)/100))] bg-gray-800 transition-transform duration-500 ease-out motion-reduce:transition-none" />
        </div>
      )}
      {children}
    </ProgressBarContext.Provider>
  )
}

/**
 * A hook that manages progress bar state and value updates.
 * Progress will continuously increase while in progress state but never reach 100%
 * until explicitly completed. Provides controls to start, complete, and reset.
 *
 * @see https://buildui.com/posts/global-progress-in-nextjs
 */
function useProgress() {
  const [state, setState] = useState<ProgressState>('initial')
  const [value, setValue] = useState(0)

  useInterval(
    () => {
      setValue((currentValue) => {
        // If we start progress but the bar is currently complete, reset it first.
        const current = currentValue === 100 ? 0 : currentValue

        /** The amount of progress to add to the bar. */
        let delta: number
        if (current === 0) {
          delta = 15
        } else if (current < 50) {
          delta = random(1, 10)
        } else {
          delta = random(1, 5)
        }

        return Math.min(current + delta, 99)
      })
    },
    state === 'in-progress' ? 750 : null,
  )

  useEffect(() => {
    if (state === 'initial') {
      // oxlint-disable-next-line react-hooks/set-state-in-effect -- TODO: fix this genuine linter error if you are working on this code.
      setValue(0)
    } else if (state === 'completing') {
      setValue(100)
    }
  }, [state])

  useEffect(() => {
    if (value === 100) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect -- TODO: fix this genuine linter error if you are working on this code.
      setState('complete')
    }
  }, [value])

  const reset = useCallback(() => {
    setState('initial')
  }, [])

  useEffect(() => {
    if (state === 'complete') {
      // oxlint-disable-next-line react-hooks/set-state-in-effect -- TODO: fix this genuine linter error if you are working on this code.
      reset()
    }
  }, [state, reset])

  function start() {
    setState('in-progress')
  }

  function done() {
    setState((state) =>
      state === 'initial' || state === 'in-progress' ? 'completing' : state,
    )
  }

  /**
   * Start a transition that will show the progress bar while the action is executing
   */
  function _startTransition(action: () => void | Promise<void>) {
    start()
    startTransition(async () => {
      await action()
      startTransition(() => {
        done()
      })
    })
  }

  return { state, value, start, done, reset, startTransition: _startTransition }
}

function random(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min
}
